import type { Movimiento } from '../lib/supabase';
import type { ObjecionComprobante, Respaldo } from '../types/comprobante';
import { respaldoObjetable } from './comprobantePlazo';

/** Un respaldo de un movimiento que quien revisa puede objetar. */
export interface CandidatoObjecion {
  readonly clave: string;
  readonly movimiento: Movimiento;
  readonly respaldo: Respaldo;
}

/** Lo que se marcó de un candidato: el motivo elegido y, con "Otro", el texto libre. */
export interface MarcaObjecion {
  readonly motivo: string;
  readonly otro: string;
}

export const MOTIVO_OTRO = 'Otro';

/**
 * Solo se ofrece lo que la base acepta objetar (`respaldoObjetable`): un comprobante adjunto de un movimiento
 * bajo la exigencia, también antes de la fecha de entrada en vigor, que no sea una cara automática ni una donación.
 */
export function candidatosDeObjecion(movimientos: readonly Movimiento[]): CandidatoObjecion[] {
  const respaldos: readonly Respaldo[] = ['pago', 'reembolso'];
  return movimientos.flatMap((movimiento) => respaldos
    .filter((respaldo) => respaldoObjetable(movimiento, respaldo))
    .map((respaldo) => ({ clave: `${movimiento.id}:${respaldo}`, movimiento, respaldo })));
}

/** Las objeciones que se envían a la base: solo las marcadas y con un motivo que no quede vacío. */
export function objecionesDeMarcas(
  candidatos: readonly CandidatoObjecion[],
  marcas: Readonly<Record<string, MarcaObjecion>>,
): ObjecionComprobante[] {
  return candidatos
    .filter((c) => marcas[c.clave] !== undefined)
    .map((c) => {
      const marca = marcas[c.clave];
      const motivo = marca.motivo === MOTIVO_OTRO ? marca.otro.trim() : marca.motivo;
      return { movimientoId: c.movimiento.id, respaldo: c.respaldo, motivo };
    })
    .filter((o) => o.motivo !== '');
}
